import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowLeft, PaperPlaneTilt, EnvelopeSimple } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!em) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: em });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col justify-center px-6 py-12" data-testid="forgot-password-page">
      <div className="max-w-md w-full mx-auto vv-fade-up">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#8A867D] hover:text-[#0D1B2A] mb-8">
          <ArrowLeft size={14} weight="bold" /> Back to sign in
        </Link>

        {!sent ? (
          <>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-medium text-[#0D1B2A] leading-tight mb-3">Forgot your password?</h1>
            <p className="text-[15px] text-[#525252] leading-relaxed mb-8">
              Type the email you signed up with. If we have an account for it, a reset link will be on its way in a minute.
              If you're not sure which email you used, try each one — the one that gets an email is the real one.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label-caps block mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-password-email"
                  className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
                />
              </div>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                data-testid="forgot-password-submit"
                className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send reset link"} <PaperPlaneTilt size={18} weight="bold" />
              </button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-password-sent" className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#DCFCE7] border-2 border-[#86EFAC] mb-5">
              <EnvelopeSimple size={30} weight="fill" className="text-[#166534]" />
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-medium text-[#0D1B2A] leading-tight mb-3">Check your inbox</h1>
            <p className="text-[15px] text-[#525252] leading-relaxed mb-6">
              If an account exists for <strong className="text-[#0D1B2A]">{email}</strong>, we've just sent it a password-reset link.
              The link expires in one hour.
            </p>
            <p className="text-sm text-[#8A867D] leading-relaxed mb-8">
              Didn't get it after a few minutes? Check spam, or try a different email. Not every guess will get a reply —
              only the email that has an actual account will.
            </p>
            <div className="flex flex-col gap-2 items-center">
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-password-try-another"
                className="text-sm font-semibold text-[#B47A3C] hover:text-[#6B4420] underline"
              >
                Try a different email
              </button>
              <Link to="/login" className="text-sm text-[#8A867D] hover:text-[#0D1B2A]">
                Back to sign in
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
